import {ComparisonCondition, Condition, GeneratedValue, Reference, ReferenceQuery, TemplateValue} from '@/model/types';

import {not} from './utils';

const createComparisonCondition =
  <TOperator extends ComparisonCondition['operator']>(operator: TOperator) =>
  <
    TField extends string | GeneratedValue | Reference | ReferenceQuery | TemplateValue,
    TValue extends string | number | Reference,
  >(
    field: TField,
    value: TValue,
  ) => {
    return {operator, field, value} as const;
  };

const createListCondition =
  <TOperator extends 'in' | 'not in'>(operator: TOperator) =>
  <TField extends string, TValue extends (string | Reference)[]>(field: TField, value: TValue) => {
    return {operator, field, value} as const;
  };

const createArrayCondition =
  <TOperator extends 'has'>(operator: TOperator) =>
  <TFields extends string, TValue extends string | Reference>(field: TFields, value: TValue) => {
    return {operator, field, value} as const;
  };

const createCollectionCondition =
  <TOperator extends 'empty' | 'not empty'>(operator: TOperator) =>
  <TField extends string[]>(fields: TField) =>
    ({operator, fields}) as const;

const isFilled = <T extends string>(field: T) => {
  return {
    operator: '!=',
    field,
    value: '',
  } as const satisfies Condition;
};

const isTrue = <TField extends string | Reference>(field: TField) => {
  return {
    operator: '=',
    field,
    value: 'true',
  } as const satisfies Condition;
};

const isFalse = <TField extends string | Reference>(field: TField) => {
  return {
    operator: '=',
    field,
    value: 'false',
  } as const satisfies Condition;
};

const isVisible = <TField extends string>(field: TField) => {
  return {
    operator: 'visible',
    field,
  } as const satisfies Condition;
};

const isHidden = <TField extends string>(field: TField) => not(isVisible(field));

export const FieldsHelper = {
  isEqual: createComparisonCondition('='),
  eq: createComparisonCondition('='),

  isDifferent: createComparisonCondition('!='),
  ne: createComparisonCondition('!='),

  isGreaterThan: createComparisonCondition('>'),
  gt: createComparisonCondition('>'),

  isGreaterThanOrEqual: createComparisonCondition('>='),
  gte: createComparisonCondition('>='),

  isLessThan: createComparisonCondition('<'),
  lt: createComparisonCondition('<'),

  isLessThanOrEqual: createComparisonCondition('<='),
  lte: createComparisonCondition('<='),

  isIn: createListCondition('in'),
  in: createListCondition('in'),

  isNotIn: createListCondition('not in'),
  notIn: createListCondition('not in'),

  isEmpty: createCollectionCondition('empty'),
  isNotEmpty: createCollectionCondition('not empty'),

  has: createArrayCondition('has'),

  isFilled: isFilled,
  isTrue: isTrue,
  isFalse: isFalse,
  isVisible: isVisible,
  isHidden: isHidden,
};
